import {Injectable} from "@angular/core";
import {catchError, map} from "rxjs/operators";
import {forkJoin, Observable, of, Subscription} from "rxjs";
import {
  BelegEntryMeta,
  GbpersonService,
  GbpfandrechtService,
  GbstandardrechtService,
  GrundstueckId,
  HistInfoMeta,
  LastRechtGrundstueckEntryMeta,
  LastRechtGruppeEntryMeta,
  LoadPfandrechtSichtGrundstueckResponseMeta,
  LoadStandardrechtSichtGrundstueckResponseMeta,
  PfandrechtBelastungMasterDetailEntryMeta,
  PfandrechtBelastungVersionDetailEntryMeta,
  PfandrechtMasterDetailEntryMeta,
  PfandrechtVersionIdMeta,
  RangregelungEntryMeta,
  RechtsgrundEntryMeta,
  StandardrechtBeteiligungMasterId,
  StandardrechtMasterDetailEntryMeta,
  StandardrechtVersionDetailEntryMeta,
} from "../gb-api";
import {TimepointCtx, TimepointService} from "./timepoint.service";
import {AppConfigService} from "./app-config.service";
import {TranslateService} from "@ngx-translate/core";
import {ComparatorService} from "../../shared/services/comparator-service";
import {HistoryMode} from "./HistoryMode";
import {calculateParallelHistInfos, calculateSerialHistInfos,} from "../../shared/functions/history.functions";
import {InfinitLookupResult} from "../../shared/components/list-loader/infinit-lookup-result";
import {RangregelungDetailVm, StandardrechtBeteiligungMasterVm,} from "./standardrecht-viewmodel.service";
import {PfandrechtVersionVm} from "./grundpfandrecht-viewmodel.service";
import {FormatConfigService} from "./format-config.service";

@Injectable()
export class RangregelungViewmodelService {
  constructor(
    private timepointService: TimepointService,
    private appConfigService: AppConfigService,
    private translateService: TranslateService,
    private rechtService: GbstandardrechtService,
    private pfandrechtService: GbpfandrechtService,
    private gbpersonService: GbpersonService,
    private comparatorService: ComparatorService,
    private appformatConfigService: FormatConfigService
  ) {
  }

  loadPfandrechtSichtByGrundstueckId(
    id: GrundstueckId
  ): Observable<RangregelungSichtGrundstueckVm> | Observable<any> {
    const z = this.timepointService.timepointCtx;
    return this.pfandrechtService
      .loadPfandrechtSichtGrundstueck(
        id.id,
        this.appConfigService.einstellungHistorischeDaten,
        true,
        z.timepointIso,
        z.timepointId
      )
      .pipe(
        map((res) => {
          const rootBelastungenVm = new Array<RootBelastungVm>();
          for (const recht of res.entries) {
            const belastungen = recht.belastungen.filter(
              (b) => b.grundstueck.grundstueckId.id === id.id
            );
            const entry = new RootBelastungVm(
              belastungen,
              id,
              recht,
              this.getPfandrechtViewModelCtx(this.timepointService.timepointCtx)
            );
            rootBelastungenVm.push(entry);
          }
          const infinitiveLookupResult = {
            result: rootBelastungenVm,
            lastJunk: false,
          };
          return new RangregelungSichtGrundstueckVm(
            infinitiveLookupResult,
            res
          );
        }),
        catchError((err) => {
          if (err.status === 403) {
            return of({entries: {result: []}});
          }
        })
      );
  }

  loadStandardrechtSichtByGrundstueckId(
    id: GrundstueckId,
    offset: number,
    limit: number,
    subscriptions: Array<Subscription>
  ): Observable<RangregelungSichtGrundstueckVm> | Observable<any> {
    const z = this.timepointService.timepointCtx;
    return this.rechtService
      .loadStandardrechtSichtGrundstueck(
        id.id,
        null,
        true,
        z.timepointIso,
        z.timepointId,
        this.appConfigService.einstellungHistorischeDaten,
        offset,
        limit
      )
      .pipe(
        map((r) => {
          const entries = new Array<LastRechtRangregelungRootEntryVm>();
          r.entries.forEach((e) => {
            const lastRechtRangregelungRootEntryVms =
              this.createViewModelByGrundstueck(e, id, subscriptions);
            entries.push(...lastRechtRangregelungRootEntryVms);
          });
          const infinitiveLookupResult = {
            result: entries,
            lastJunk: r.lastJunk,
          };
          return new RangregelungSichtGrundstueckVm(infinitiveLookupResult, r);
        }),
        catchError((err) => {
          if (err.status === 403) {
            return of({entries: {result: [], lastJunk: true}});
          }
        })
      );
  }

  loadSichtByGrundstueckId(
    id: GrundstueckId,
    offset: number,
    limit: number,
    subscriptions: Array<Subscription>
  ): Observable<RangregelungSichtGrundstueckVm> {
    if (offset === 0) {
      return forkJoin(
        this.loadPfandrechtSichtByGrundstueckId(id),
        this.loadStandardrechtSichtByGrundstueckId(
          id,
          offset,
          limit,
          subscriptions
        )
      ).pipe(
        map(
          ([pfandrechte, standardrechte]: [
            RangregelungSichtGrundstueckVm,
            RangregelungSichtGrundstueckVm
          ]) => {
            const infinitiveLookupResult = {
              result: pfandrechte.entries.result.concat(
                standardrechte.entries.result
              ),
              // Potentielle Pfandrechte, die noch geladen werden müssen
              lastJunk: standardrechte.entries.lastJunk && pfandrechte.entries.result.length === 0,
            };
            return new RangregelungSichtGrundstueckVm(
              infinitiveLookupResult,
              {}
            );
          }
        )
      );
    }
    return this.loadStandardrechtSichtByGrundstueckId(
      id,
      offset,
      limit,
      subscriptions
    );
  }

  private createViewModelByGrundstueck(
    recht: StandardrechtMasterDetailEntryMeta,
    id: GrundstueckId,
    subscriptions: Array<Subscription>
  ): LastRechtRangregelungRootEntryVm[] {
    const res = new Array<LastRechtRangregelungRootEntryVm>();
    const lastRechtGruppen = this.groupLastRechtGruppenByBelastungsArt(
      recht,
      id
    );
    lastRechtGruppen.forEach((v) => {
      const timpoint = this.timepointService.timepointCtx;
      const lastRechtRangregelungRootEntryVm =
        new LastRechtRangregelungRootEntryVm(
          recht,
          v,
          id,
          this.getViewModelCtx(timpoint, subscriptions)
        );
      if (
        lastRechtRangregelungRootEntryVm.rangregelungen.length === 0 &&
        lastRechtRangregelungRootEntryVm.object.histInfo.bisTimePoint
      ) {
        return;
      }
      res.push(lastRechtRangregelungRootEntryVm);
    });
    return res;
  }

  private groupLastRechtGruppenByBelastungsArt(
    recht: StandardrechtMasterDetailEntryMeta,
    grundstueckId: GrundstueckId
  ) {
    const res = new Map<String, Array<LastRechtGruppeEntryMeta>>();

    const belastungsArt: LastRechtGrundstueckEntryMeta.BelastungArtEnum[] = [];
    let key: any = null;
    for (const lastRechtGruppe of recht.lastRechtGruppen) {
      const grundstueckEntries = extractSelfReferences(
        [lastRechtGruppe],
        grundstueckId,
        recht.rechtArt,
        this.appformatConfigService
      );
      if (!grundstueckEntries || grundstueckEntries.length === 0) {
        continue;
      }
      belastungsArt.push(grundstueckEntries[0].belastungArt);
      if (!key) {
        key = JSON.stringify({
          id: grundstueckEntries[0].standardrechtBeteiligungMasterId,
          belastungsArt: belastungsArt,
        });
      }
      let list = res.get(key);
      if (!list) {
        list = new Array<LastRechtGruppeEntryMeta>();
      }
      list.push(lastRechtGruppe);
      res.set(key, list);
    }
    return res;
  }

  private getPfandrechtViewModelCtx(z) {
    return <PfandrechtViewModelCtx>{
      timepointId: z.timepointId,
      timepoint: z.timepointCtx,
      einstellungHistorischeDaten:
      this.appConfigService.einstellungHistorischeDaten,
      translateService: this.translateService,
      comparatorService: this.comparatorService,
    };
  }

  private getViewModelCtx(
    timpoint: TimepointCtx,
    subscriptions: Array<Subscription>
  ) {
    return <ViewModelCtx>{
      timepointId: timpoint.timepointId,
      timepointIso: timpoint.timepointIso,
      einstellungHistorischeDaten:
      this.appConfigService.einstellungHistorischeDaten,
      translateService: this.translateService,
      personService: this.gbpersonService,
      comparatorService: this.comparatorService,
      subscriptions: subscriptions,
      appformatConfigService: this.appformatConfigService,
    };
  }
}

interface PfandrechtViewModelCtx {
  translateService: TranslateService;
  personService: GbpersonService;
  comparatorService: ComparatorService;
  einstellungHistorischeDaten: HistoryMode.HistoryMode;
  timepoint: string;
  timepointId: string;
}

export interface ViewModelCtx {
  translateService: TranslateService;
  personService: GbpersonService;
  comparatorService: ComparatorService;
  einstellungHistorischeDaten: HistoryMode.HistoryMode;
  timepointIso: string;
  timepointId: string;
  subscriptions: Array<Subscription>;
  appformatConfigService: FormatConfigService;
}

export class RangregelungSichtGrundstueckVm {
  private readonly response: LoadStandardrechtSichtGrundstueckResponseMeta;

  constructor(
    public entries: InfinitLookupResult<AbstractRangregelungVm>,
    response: LoadStandardrechtSichtGrundstueckResponseMeta
  ) {
    this.response = response;
    this.entries = entries;
  }

  get ersterfassungUebergeben() {
    return this.response.ersterfassungUebergeben;
  }
}

export abstract class AbstractRangregelungVm {
  constructor() {
  }

  public abstract get rangregelungDatum(): string;

  public abstract get isPfandrecht(): boolean;

  public abstract get rangregelungen(): any[];

  public abstract get rechtMaster(): any;

  public abstract get lastVersion(): any;

  public abstract get erstellzeitpunkt(): Date;
}

export class LastRechtRangregelungRootEntryVm extends AbstractRangregelungVm {
  belastungsArt: LastRechtGrundstueckEntryMeta.BelastungArtEnum;
  standardRechtMasterVm: StandardrechtMasterVm;
  selfReferences: LastRechtGrundstueckEntryMeta[];
  rangregelungen: RangregelungDetailVm[];
  belastungsArten: string[];

  constructor(
    public object: StandardrechtMasterDetailEntryMeta,
    private lastRechtGruppen: LastRechtGruppeEntryMeta[],
    public grundstueckId: GrundstueckId,
    private ctx: ViewModelCtx
  ) {
    super();
    this.selfReferences = extractSelfReferences(
      lastRechtGruppen,
      this.grundstueckId,
      object.rechtArt,
      ctx.appformatConfigService
    );
    const histInfo = calculateParallelHistInfos(
      this.selfReferences.map((r) => r.histInfo)
    );
    this.belastungsArten = this.calculateBelastungsart();
    this.belastungsArt = this.selfReferences[0].belastungArt;
    this.standardRechtMasterVm = new StandardrechtMasterVm(object, histInfo);
    this.rangregelungen = this.calculateRangregelungen();
  }

  public get rangregelungDatum(): string {
    return this.standardRechtMasterVm.versionenVm[0].object.beginndatum;
  }

  public get isPfandrecht(): boolean {
    return false;
  }

  public get erstellzeitpunkt(): Date {
    return this.standardRechtMasterVm.object.histInfo.erstellzeitpunkt;
  }

  public get rechtMaster(): StandardrechtMasterDetailEntryMeta {
    return this.standardRechtMasterVm.object;
  }

  public get rechtsgruende(): RechtsgrundEntryMeta[] {
    if (!(this.belastungsArt === "BELASTET")) {
      return Array<RechtsgrundEntryMeta>();
    }
    const rechtsgruende = new Array<RechtsgrundEntryMeta>();
    for (const selfReference of this.selfReferences) {
      rechtsgruende.push(...selfReference.rechtsgruende);
    }
    return rechtsgruende;
  }

  public get isBelastet(): boolean {
    return this.belastungsArt === "BELASTET";
  }

  getBelastungsartKurz(
    belastungArt: LastRechtGrundstueckEntryMeta.BelastungArtEnum
  ): string {
    switch (belastungArt) {
      case "BELASTET":
        return "Belastet";
      case "BERECHTIGT":
        return "Berechtigt";
      case "BELASTET_BERECHTIGT":
        return "BelastetBerechtigt";
      case "DOMINANTINDET":
        return "";
      default:
        return "";
    }
  }

  private calculateBelastungsart(): string[] {
    const belastungArten: string[] = [];
    const belastungArtMap = new Map<string, boolean>();
    belastungArtMap.set("Belastet", false);
    belastungArtMap.set("Berechtigt", false);
    belastungArtMap.set("BelastetBerechtigt", false);
    for (const lastRechtGrundstueck of this.selfReferences) {
      belastungArtMap.set(
        this.getBelastungsartKurz(lastRechtGrundstueck.belastungArt),
        true
      );
    }
    belastungArtMap.forEach((value: boolean, key: string) => {
      if (value) {
        belastungArten.push(key);
      }
    });
    return belastungArten;
  }

  private calculateRangregelungen(): RangregelungDetailVm[] {
    const res = new Array<RangregelungDetailVm>();
    for (const beteiligung of this.relevantStandardrechtBeteiligungen()) {
      for (const rangregelung of beteiligung.object.rangregelung) {
        if (rangregelung.vorgaengerPfandrechtBelastung) {
          res.push(
            new RangregelungDetailVm(
              rangregelung,
              rangregelung.grundstueck.grundstueckId.id === this.grundstueckId.id
            )
          );
        }
      }
    }
    return res;
  }

  private relevantStandardrechtBeteiligungen(): StandardrechtBeteiligungMasterVm[] {
    const ids = this.selfReferences.map(
      (r) => r.standardrechtBeteiligungMasterId
    );
    return this.standardRechtMasterVm.getRelevantStandardrechtMaster(ids);
  }

  get lastVersion(): any {
    return this.standardRechtMasterVm.lastVersion;
  }
}

export class StandardrechtMasterVm {
  versionenVm: StandardrechtVersionVm[];
  beteiligungsMastersVm: StandardrechtBeteiligungMasterVm[];

  constructor(
    public object: StandardrechtMasterDetailEntryMeta,
    private rechteBeziehungHistInfo: HistInfoMeta
  ) {
    this.versionenVm = new Array<StandardrechtVersionVm>();
    for (let i = 0; i < object.versions.length; i++) {
      const isLastVersion = i === object.versions.length - 1;
      if (isLastVersion) {
        this.versionenVm.push(
          new StandardrechtVersionVm(
            object.versions[i],
            object,
            this.rechteBeziehungHistInfo,
            isLastVersion
          )
        );
      }
    }
    this.beteiligungsMastersVm = object.beteiligungMasters.map(
      (b) => new StandardrechtBeteiligungMasterVm(b)
    );
    this.object.beteiligungMasters.map(
      (b) => new StandardrechtBeteiligungMasterVm(b)
    );
  }

  get lastVersion(): StandardrechtVersionVm {
    return this.versionenVm[this.versionenVm.length - 1];
  }

  getRelevantStandardrechtMaster(
    masterIds: Array<StandardrechtBeteiligungMasterId>
  ): StandardrechtBeteiligungMasterVm[] {
    const masterIdsAsString = masterIds.map((i) => i.id);
    return this.beteiligungsMastersVm.filter((e) =>
      masterIdsAsString.includes(e.object.id.id)
    );
  }
}

export class StandardrechtVersionVm {
  readonly beleg: BelegEntryMeta;

  constructor(
    readonly object: StandardrechtVersionDetailEntryMeta,
    private master: StandardrechtMasterDetailEntryMeta,
    private rechteBeziehungHistInfo: HistInfoMeta,
    private lastVersion: boolean
  ) {
    this.beleg = object.beleg;
  }

  getQueryParams() {
    return {id: this.master.id.id, versionId: this.object.id.id};
  }

  get histInfo(): HistInfoMeta {
    if (this.lastVersion) {
      return calculateSerialHistInfos([
        this.object.histInfo,
        this.rechteBeziehungHistInfo,
      ]);
    } else {
      return this.object.histInfo;
    }
  }
}

export class GrundpfandrechtSichtGrundstueckVm {
  private readonly response: LoadPfandrechtSichtGrundstueckResponseMeta;

  constructor(
    public entries: RootBelastungVm[],
    response: LoadPfandrechtSichtGrundstueckResponseMeta
  ) {
    this.response = response;
    this.entries = entries;
  }

  get ersterfassungUebergeben() {
    return this.response.ersterfassungUebergeben;
  }
}

export class RootBelastungVm extends AbstractRangregelungVm {
  pfandrechtMasterVm: PfandrechtMasterVm;

  constructor(
    public pfandrechtBelastungMasters: PfandrechtBelastungMasterDetailEntryMeta[],
    public grundstueckId: GrundstueckId,
    private pfandrechtMaster: PfandrechtMasterDetailEntryMeta,
    private ctx: PfandrechtViewModelCtx
  ) {
    super();
    this.pfandrechtMasterVm = new PfandrechtMasterVm(
      pfandrechtMaster,
      pfandrechtBelastungMasters
    );
  }

  public get rangregelungDatum(): string {
    return this.pfandrechtMasterVm.versionenVm[0].pfandrechtBelastungVersions[0]
      .rangbestimmendesDatum;
  }

  public get isPfandrecht(): boolean {
    return true;
  }

  public get rechtMaster(): PfandrechtMasterDetailEntryMeta {
    return this.pfandrechtMasterVm.object;
  }

  public get erstellzeitpunkt(): Date {
    return this.pfandrechtMasterVm.object.histInfo.erstellzeitpunkt;
  }

  get lastVersion() {
    const res = new Array<PfandrechtBelastungVersionDetailEntryMeta>();
    for (const pfandrechtBelastungMaster of this.pfandrechtBelastungMasters) {
      res.push(...pfandrechtBelastungMaster.versions);
    }
    return res[res.length - 1];
  }

  get rangregelungen() {
    const res = new Array<RangregelungEntryMeta>();
    for (const pfandrechtBelastungMaster of this.pfandrechtBelastungMasters) {
      res.push(...pfandrechtBelastungMaster.rangregelung);
    }
    return res;
  }
}

export class PfandrechtMasterVm {
  versionenVm: PfandrechtVersionVm[];

  constructor(
    public object: PfandrechtMasterDetailEntryMeta,
    private pfandrechtBelastungMasters: PfandrechtBelastungMasterDetailEntryMeta[]
  ) {
    const pfandrechtBelastungVersions = new Array<PfandrechtBelastungVersionDetailEntryMeta>();
    for (const pfandrechtBelastungMaster of this.pfandrechtBelastungMasters) {
      pfandrechtBelastungVersions.push(...pfandrechtBelastungMaster.versions);
    }
    this.versionenVm = new Array<PfandrechtVersionVm>();
    const lastVersion = object.versions[object.versions.length - 1];
    const pfandrechtBelastungVersionen = this.calculatePfandrechtBelastungVersion(lastVersion.id, pfandrechtBelastungVersions);
    this.versionenVm.push(new PfandrechtVersionVm(lastVersion, object, pfandrechtBelastungVersionen));
  }

  private calculatePfandrechtBelastungVersion(
    versionId: PfandrechtVersionIdMeta,
    pfandrechtBelastungVersions: PfandrechtBelastungVersionDetailEntryMeta[]
  ) {
    return pfandrechtBelastungVersions.filter((v) =>
      v.pfandrechtVersionIds.map((y) => y.id).includes(versionId.id)
    );
  }
}

export enum NachgangserklaerungEnum {
  KEINE = "KEINE",
  NUR_LASTEN = "NUR_LASTEN",
  NUR_RECHTE = "NUR_RECHTE",
  LASTEN_UND_RECHTE = "LASTEN_UND_RECHTE",
}

function extractSelfReferences(
  lastRechtGruppen: LastRechtGruppeEntryMeta[],
  grundstueckId: GrundstueckId,
  art: StandardrechtMasterDetailEntryMeta.RechtArtEnum,
  appformatConfigService: FormatConfigService
): LastRechtGrundstueckEntryMeta[] {
  const parameter = getParameterMap(appformatConfigService).get(art);
  const res = new Array<LastRechtGrundstueckEntryMeta>();
  for (const lastRechtGruppe of lastRechtGruppen) {
    if (
      parameter === NachgangserklaerungEnum.NUR_LASTEN ||
      parameter === NachgangserklaerungEnum.LASTEN_UND_RECHTE
    ) {
      res.push(
        ...extractLastRechtEntriesForGrundstueck(
          lastRechtGruppe.grundstueckeLast,
          grundstueckId
        )
      );
    }
    if (
      parameter === NachgangserklaerungEnum.NUR_RECHTE ||
      parameter === NachgangserklaerungEnum.LASTEN_UND_RECHTE
    ) {
      res.push(
        ...extractLastRechtEntriesForGrundstueck(
          lastRechtGruppe.grundstueckeRecht,
          grundstueckId
        )
      );
    }
    if (parameter !== NachgangserklaerungEnum.KEINE) {
      res.push(
        ...extractLastRechtEntriesForGrundstueck(
          lastRechtGruppe.grundstueckeLastRecht,
          grundstueckId
        )
      );
    }
  }
  return res;
}

function getParameterMap(
  appformatConfigService: FormatConfigService
): Map<StandardrechtMasterDetailEntryMeta.RechtArtEnum, any> {
  const parameterMap = new Map<StandardrechtMasterDetailEntryMeta.RechtArtEnum, any>();
  const rechtArtEnum = StandardrechtMasterDetailEntryMeta.RechtArtEnum;
  const parameters = appformatConfigService.parameterConfig.parameters;
  parameterMap.set(
    rechtArtEnum.ANMERKUNG,
    parameters.RANGREGELUNG_NACHGANG_ERLAUBEN_FUER_ANMERKUNGEN
  );
  parameterMap.set(
    rechtArtEnum.DIENSTBARKEIT,
    parameters.RANGREGELUNG_NACHGANG_ERLAUBEN_FUER_DIENSTBARKEIT
  );
  parameterMap.set(
    rechtArtEnum.SDRDIENSTBARKEIT,
    parameters.RANGREGELUNG_NACHGANG_ERLAUBEN_FUER_SD_RECHTE
  );
  parameterMap.set(
    rechtArtEnum.VORMERKUNG,
    parameters.RANGREGELUNG_NACHGANG_ERLAUBEN_FUER_VORMERKUNGEN
  );
  parameterMap.set(
    rechtArtEnum.GRUNDLAST,
    parameters.RANGREGELUNG_NACHGANG_ERLAUBEN_FUER_GRUNDLAST
  );
  return parameterMap;
}

function extractLastRechtEntriesForGrundstueck(
  entries: Array<LastRechtGrundstueckEntryMeta>,
  grundstueckId: GrundstueckId
): LastRechtGrundstueckEntryMeta[] {
  return entries.filter(
    (gl) => gl.grundstueck.grundstueckId.id === grundstueckId.id
  );
}
